import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAppSelector } from "@/store/hooks";
import fixURI from "@/utils/fixURI";
import Big from "big.js";
import { SendEventForHits } from "instantsearch.js/es/lib/utils";
import { Hit } from "instantsearch.js/es/types";

import CurrencyDisplay from "@/app/components/currencyDisplay";
import AddToCart from "@/components/products-tile/addToCart";
import Packaging from "@/components/products-tile/packaging";
import ProductImageLegacy from "@/components/products-tile/productImageLegacy";
import StarRatingOrPlaceholder from "@/components/products-tile/starRatingOrPlaceholder";
import { ViewMode } from "@/components/view-toggle/viewToggle";
import { DeviceType } from "@/types/search";

import CompareProductCheckbox from "@/components/products-tile/compareProductCheckbox";
import LastPurchased from "@/components/last-purchased/lastPurchased";
import { makeGetBestPriceByMpIdSelector } from "@/store/searchSlice";
import Image from "next/image";
import styles from "./productsGrid.module.scss";

/* 
NOTE
We are re-implementing `<ProductImage />
*/

type ProductItemProps = {
  hit: ProductHit;
  viewMode?: ViewMode;
  deviceType: DeviceType;
  sendEvent: SendEventForHits;
  /** Defaults to "search" */
  pageType?: "search" | "category" | "buy-get";
  buyGetBaseURI?: string;
  disableAddToCart?: boolean;
  hideBadges?: boolean;
  hideLastPurchased?: boolean;
  hidePrice?: boolean;
  isSponsored?: boolean;
};

/**
 * TODO Trim this to what we actaully send now
 */
export interface ProductHit extends Hit {
  description_short: string;
  title: string;
  mp_id: number;
  image_link: string;
  brand: string;
  attributes: { packaging: string };
  retail_price: number;
  price: number;
  manufacturer: string;
  general_rating?: number | undefined;
  number_of_general_ratings?: number;
  category: { [key: string]: string };
  link: string;
  inventory_quantity: number;

  /**
   * Topsort specific fiels
   */
  vendorId: string;
  isSponsored: boolean;
  resolvedBidId: string;
}
const ProductItem: React.FC<ProductItemProps> = ({
  hit,
  viewMode,
  deviceType,
  sendEvent,
  disableAddToCart = false,
  hideBadges = false,
  hideLastPurchased = false,
  hidePrice = false,
  pageType = "search",
  buyGetBaseURI,
}) => {
  const isListView = viewMode === "list" || deviceType !== "desktop";
  const [isFreeShippingEligible, setIsFreeShippingEligible] = useState(false);
  const [isFastDeliveryEligible, setIsFastDeliveryEligible] = useState(false);

  const isMobile = deviceType === "mobile";
  const selectBestPriceByMpId = useMemo(makeGetBestPriceByMpIdSelector, []);
  const productFromServer = useAppSelector((state) => selectBestPriceByMpId(state, hit.mp_id));
  const shippingRegionId = useAppSelector((state) => state.searchSlice.shippingRegionId);

  const retailPrice = useMemo(() => {
    try {
      if (!productFromServer) {
        if (new Big(hit.price).gte(hit.retail_price)) {
          return 0;
        } else {
          return hit.retail_price;
        }
      } else {
        if (!productFromServer.retailPrice) {
          return hit.retail_price;
        } else if (new Big(productFromServer.unitPrice).gte(productFromServer.retailPrice)) {
          return 0;
        } else {
          return productFromServer.retailPrice;
        }
      }
    } catch (error) {
      console.error(error);
      return hit.retail_price;
    }
  }, [hit.price, hit.retail_price, productFromServer]);

  const isFetchComplete = useRef(false);

  const getTopsortAttributes = (
    isSponsored: boolean,
    mp_id: number,
    resolvedBidId: string,
  ): Record<string, string | number | boolean> => {
    return isSponsored
      ? {
          "data-ts-resolved-bid": resolvedBidId,
          "data-ts-product": mp_id,
        }
      : {};
  };

  const sponsoredAttributes = useMemo(() => {
    return getTopsortAttributes(hit.isSponsored, hit.mp_id, hit.resolvedBidId);
  }, [hit.isSponsored, hit.mp_id, hit.resolvedBidId, hit.sponsoredPath]);

  useEffect(() => {
    setIsFreeShippingEligible(hit.free_shipping_regions?.includes(shippingRegionId) || false);
  }, [hit.free_shipping_regions, shippingRegionId]);

  useEffect(() => {
    setIsFastDeliveryEligible(hit.fast_delivery_regions?.includes(shippingRegionId) || false);
  }, [hit.fast_delivery_regions, shippingRegionId]);

  const onClickCallback = useCallback(() => {
    sendEvent("click", hit, "Product Clicked");
  }, [hit, sendEvent]);

  const productLink = useMemo(() => {
    let link = fixURI(hit.link);
    if (pageType === "buy-get" && buyGetBaseURI) {
      link = buyGetBaseURI + link.replace("/ec", "");
    }

    const params: string[] = [];

    if (hit.__queryID) {
      // link = link + `?queryID=${hit.__queryID}`;
      params.push(`queryID=${hit.__queryID}`);
    }

    if (hit.isSponsored) {
      params.push(`tsid=${hit.vendorId}`);
    }

    if (params.length) {
      link = link + "?" + params.join("&");
    }

    return link;
  }, [buyGetBaseURI, hit.__queryID, hit.isSponsored, hit.vendorId, hit.link, pageType]);

  return (
    <div className={`${isListView ? styles.listView : "d-block"} ${styles.customCard}`} {...sponsoredAttributes}>
      <div className={`${isListView ? "d-flex flex-column" : ""}${!hit.isSponsored ? styles.nonSponsoredItem : ""}`}>
        {hit.isSponsored && !isListView && (
          <div className={styles.gridSponsoredLabelContainer}>
            <span className={styles.sponsoredLabel}>Sponsored</span>
          </div>
        )}

        <a
          className={`${isListView ? styles.imgContainerWithLabel : ""}`}
          href={productLink}
          onClick={onClickCallback}
          {...(hit.isSponsored ? { "data-ts-clickable": true } : {})}
        >
          <div className={`d-flex justify-content-center ${styles.productImageContainer}`}>
            <ProductImageLegacy
              src={hit?.image_link}
              className={styles.productImage}
              alt={hit.title}
              width={isListView ? (isMobile ? 70 : 130) : 130}
              height={isListView ? (isMobile ? 70 : 140) : 140}
              objectFit="contain"
            />
          </div>
        </a>
      </div>
      <div className="card-body">
        {hit.isSponsored && isListView && (
          <div className={styles.sponsoredLabelContainer}>
            <span className={styles.sponsoredLabel}>Sponsored</span>
          </div>
        )}
        <h5 className={`${styles.productGridTitle} fw-bolder`}>
          {pageType === "buy-get" ? productFromServer?.name : hit.brand}
        </h5>

        <a
          href={productLink}
          id="product-description"
          className={`${styles.productDescription} card-text mb-1 ${isListView ? styles.productListTitle : " "}`}
          onClick={onClickCallback}
          {...(hit.isSponsored ? { "data-ts-clickable": true } : {})}
        >
          {hit.title}
        </a>
        <Packaging packaging={hit?.attributes.packaging} />
        <StarRatingOrPlaceholder
          general_rating={hit.general_rating}
          number_of_general_ratings={hit.number_of_general_ratings}
        />

        {!hideBadges && (
          <div className={styles.badges}>
            <div
              className={`${styles.badgeLabel} ${styles.badgeFreeShipping} freeShippingBadge`}
              title="This product is eligible for free shipping based on your delivery location."
            >
              {isFreeShippingEligible && (
                <>
                  <Image
                    className={styles.badgeIcon}
                    src="/media/dental/common/icons/free_shipping_icon.svg"
                    height={13}
                    width={13}
                    alt="free shipping icon"
                  />
                  <p className="m-0">Free Shipping</p>
                </>
              )}
            </div>

            <div
              className={`${styles.badgeLabel} ${styles.badgeFastDelivery} fastDeliveryBadge`}
              title="Delivery within 4 business days or less."
            >
              {isFastDeliveryEligible && (
                <>
                  <Image
                    className={styles.badgeIcon}
                    src="/media/dental/common/icons/fast_delivery_icon.svg"
                    height={13}
                    width={13}
                    alt="fast delivery Icon"
                  />
                  <p className="m-0">Fast Delivery</p>
                </>
              )}
            </div>
          </div>
        )}

        {!hideLastPurchased && (
          <LastPurchased
            date={productFromServer?.lastPurchaseDate}
            mpId={hit.mp_id}
            slotClass={styles.lastPurchased}
            hasContentClass={styles.hasContent}
            isTopsortClickable={hit.isSponsored}
          />
        )}
        {!hidePrice && (
          <div className="py-2">
            <span className={styles.price}>
              <CurrencyDisplay
                value={
                  hit.isSponsored ? hit.price : productFromServer?.unitPrice ? productFromServer.unitPrice : hit.price
                }
              />
            </span>
            <span className={`${styles.retailPrice} px-2`}>
              {retailPrice > 0 && <CurrencyDisplay value={retailPrice} />}
            </span>
          </div>
        )}
        {disableAddToCart === false && (
          <AddToCart hit={hit} isListView={isListView} deviceType={deviceType} sendEvent={sendEvent} />
        )}
        <div className={styles.compareCheckbox}>
          <CompareProductCheckbox hit={hit} />
        </div>
      </div>
    </div>
  );
};

export default ProductItem;
